import { platformClient } from './platform.client';
import { serviceUrls } from '../../../config/serviceUrls';
import { protectedFetch } from '../../../utils/authSession';
import {
  Class,
  CreateStudentRequest,
  LinkCodeResponse,
  PatchSchoolFeatureFlagsRequest,
  PatchSchoolFeatureFlagsResponse,
  School,
  Student,
} from '../types';
import { StatusResponse } from '../../../types/api';

export const getSchool = async (schoolId: string): Promise<School> => {
  const response = await platformClient.get<School>(`/schools/${schoolId}`);
  return response.data;
};

export const updateSchool = async (
  schoolId: string,
  data: Partial<School>
): Promise<StatusResponse> => {
  const response = await platformClient.patch<StatusResponse>(`/schools/${schoolId}`, data);
  return response.data;
};

/** Dev Team: full `feature_flags` snapshot in body. Response may be partial — callers should refetch GET /schools/:id if they need a full School. */
export const patchSchoolFeatureFlags = async (
  schoolId: string,
  body: PatchSchoolFeatureFlagsRequest
): Promise<PatchSchoolFeatureFlagsResponse> => {
  const response = await platformClient.patch<PatchSchoolFeatureFlagsResponse>(
    `/schools/${schoolId}/feature-flags`,
    body
  );
  return response.data;
};

export const listClasses = async (schoolId: string): Promise<Class[]> => {
  const response = await platformClient.get<Class[]>(`/schools/${schoolId}/classes`);
  return response.data;
};

export interface CreateClassRequest {
  name: string;
  grade: string;
  section?: string;
  academic_year?: string;
  status?: string;
}

export const createClass = async (schoolId: string, data: CreateClassRequest): Promise<Class> => {
  const response = await platformClient.post<Class>(`/schools/${schoolId}/classes`, data);
  return response.data;
};

export const createStudent = async (
  schoolId: string,
  data: CreateStudentRequest
): Promise<Student> => {
  const response = await platformClient.post<Student>(`/schools/${schoolId}/students`, data);
  return response.data;
};

export const listStudents = async (schoolId: string): Promise<Student[]> => {
  const response = await platformClient.get<Student[]>(`/schools/${schoolId}/students`);
  return response.data;
};

export const inviteTeacher = async (
  schoolId: string,
  email: string
): Promise<StatusResponse> => {
  const response = await platformClient.post<StatusResponse>(
    `/schools/${schoolId}/invites/teachers`,
    { email }
  );
  return response.data;
};

export const inviteParent = async (
  schoolId: string,
  email: string,
  studentId?: string
): Promise<StatusResponse> => {
  const response = await platformClient.post<StatusResponse>(
    `/schools/${schoolId}/invites/parents`,
    { email, student_id: studentId }
  );
  return response.data;
};

export const inviteStudent = async (
  schoolId: string,
  email: string,
  studentId?: string
): Promise<StatusResponse> => {
  const response = await platformClient.post<StatusResponse>(
    `/schools/${schoolId}/invites/students`,
    { email, student_id: studentId }
  );
  return response.data;
};

export const generateLinkCode = async (
  schoolId: string,
  studentId: string
): Promise<LinkCodeResponse> => {
  const response = await platformClient.post<LinkCodeResponse>(
    `/schools/${schoolId}/students/${studentId}/link-code`
  );
  return response.data;
};

export const linkParentStudent = async (
  schoolId: string,
  data: {
    parent_user_id: string;
    student_id: string;
    relationship?: string;
  }
): Promise<StatusResponse> => {
  const response = await platformClient.post<StatusResponse>(
    `/schools/${schoolId}/link-parent-student`,
    data
  );
  return response.data;
};

/** Student-facing: /me/school-language wrapper */
export interface StudentSchoolLanguage {
  ui_default: string;
  allowed: string[];
  preferred_dialect_keys: string[];
  default_dialect_key: string;
}

const dialectInflight = new Map<string, Promise<Response>>();
const schoolLangInflight = new Map<string, Promise<StudentSchoolLanguage>>();
const schoolLangCache = new Map<string, { data: StudentSchoolLanguage; fetchedAt: number }>();
export const SCHOOL_LANGUAGE_TTL_MS = 5 * 60 * 1000;

function schoolLanguageCacheKey(): string {
  try {
    return (localStorage.getItem('X_TENANT_ID') || '').trim() || 'no-tenant';
  } catch {
    return 'no-tenant';
  }
}

export function clearSchoolLanguageCache(): void {
  schoolLangCache.clear();
  schoolLangInflight.clear();
}

/** Deduped GET /v1/tutor/dialect-options (StrictMode-safe). */
export async function fetchDialectOptions(): Promise<Response> {
  const key = '__default__';
  const existing = dialectInflight.get(key);
  if (existing) return existing;

  const base = serviceUrls.gateway.replace(/\/+$/, '');
  const url = `${base}/v1/tutor/dialect-options`;
  const run = protectedFetch(url);
  dialectInflight.set(key, run);
  try {
    return await run;
  } finally {
    if (dialectInflight.get(key) === run) {
      dialectInflight.delete(key);
    }
  }
}

/** Deduped GET /me/school-language (StrictMode-safe, tenant-keyed TTL). */
export async function fetchSchoolLanguage(): Promise<StudentSchoolLanguage> {
  const key = schoolLanguageCacheKey();
  const cached = schoolLangCache.get(key);
  if (cached && Date.now() - cached.fetchedAt < SCHOOL_LANGUAGE_TTL_MS) {
    return cached.data;
  }
  const existing = schoolLangInflight.get(key);
  if (existing) return existing;

  const run = platformClient
    .get<StudentSchoolLanguage>('/me/school-language')
    .then((response) => {
      schoolLangCache.set(key, { data: response.data, fetchedAt: Date.now() });
      return response.data;
    });

  schoolLangInflight.set(key, run);
  try {
    return await run;
  } catch (err) {
    schoolLangCache.delete(key);
    throw err;
  } finally {
    if (schoolLangInflight.get(key) === run) {
      schoolLangInflight.delete(key);
    }
  }
}

export const getMySchoolLanguage = fetchSchoolLanguage;

/** Optional: GET /me/school-dialects (if backend provides). Returns ordered dialect metadata for students. */
export interface StudentSchoolDialect {
  key: string;
  name: string;
  language?: string | null;
  region?: string | null;
  category?: string | null;
}

export interface StudentSchoolDialectsResponse {
  default_dialect_key: string;
  dialects: StudentSchoolDialect[];
}

export const getMySchoolDialects = async (): Promise<StudentSchoolDialectsResponse> => {
  const response = await platformClient.get<StudentSchoolDialectsResponse>('/me/school-dialects');
  return response.data;
};

export type SchoolUiLangCode = 'en' | 'ta' | 'ms';

/**
 * Update school UI language via existing PATCH /schools/{id}/school-settings.
 * Ensures ui_default is always included in allowed.
 */
export const updateSchoolUiLanguage = async (
  schoolId: string,
  uiDefault: SchoolUiLangCode,
  allowed: SchoolUiLangCode[] = ['en', 'ta', 'ms']
): Promise<School> => {
  const allowedSet = new Set<SchoolUiLangCode>(allowed.length ? allowed : ['en', 'ta', 'ms']);
  allowedSet.add(uiDefault);
  await platformClient.patch(`/schools/${schoolId}/school-settings`, {
    language: { ui_default: uiDefault, allowed: Array.from(allowedSet) },
  });
  return getSchool(schoolId);
};

/** Patch only preferred dialect keys for a school (Dev Team). Sends only { language: { preferred_dialect_keys: [...] } } */
export const patchSchoolLanguage = async (
  schoolId: string,
  payload: { language: { preferred_dialect_keys: string[] } }
): Promise<School> => {
  const response = await platformClient.patch<School>(`/schools/${schoolId}/language`, payload);
  return response.data;
};


export type SchoolActivityLogRow = {
  id?: string;
  event_type?: string;
  actor_user_id?: string | null;
  target?: Record<string, unknown> | null;
  metadata?: Record<string, unknown>;
  created_at?: string;
};

export async function getSchoolActivityLogs(
  schoolId: string,
  params?: {
    limit?: number;
    skip?: number;
    event_type?: string;
    actor_user_id?: string;
    search?: string;
    from_ts?: string;
    to_ts?: string;
  }
): Promise<{ ok: boolean; items: SchoolActivityLogRow[]; total: number; skip: number; limit: number }> {
  const response = await platformClient.get(`/schools/${schoolId}/activity-logs`, { params });
  return response.data;
}

